import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Pagination,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { QuantityCheck, SiteInspection } from 'services/ParkingAgentService';

const STATUS = {
  match: '일치',
  shortage: '누락·부족',
  excess: '초과',
  unavailable: '확인 불가',
  not_applicable: '비교 대상 아님',
} as const;

function QuantityResult({
  label,
  check,
  unit,
}: {
  label: string;
  check: QuantityCheck;
  unit: string;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography>
        {check.actual === undefined ? '—' : `${check.actual}${unit}`}
      </Typography>
      <Chip
        size="small"
        label={STATUS[check.status]}
        color={
          check.status === 'match'
            ? 'success'
            : check.status === 'shortage' || check.status === 'excess'
              ? 'warning'
              : 'default'
        }
      />
      {check.difference !== undefined && check.difference !== 0 && (
        <Typography variant="body2">
          기준 대비 {check.difference > 0 ? '+' : ''}
          {check.difference}
          {unit}
        </Typography>
      )}
      {check.reason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {check.reason}
        </Typography>
      )}
    </Box>
  );
}

const ReferenceReport: React.FC<{
  report: NonNullable<SiteInspection['reference']>;
}> = ({ report }) => {
  const [page, setPage] = useState(1);
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(report.comparisons.length / 12)),
  );
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
        기준 문서 대조 · {report.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {report.source === 'network' ? 'IP/네트워크' : '자재 문서'} ·{' '}
        {report.quantity_mode === 'rows'
          ? '장비 1행당 1개'
          : report.quantity_mode === 'ip_range'
            ? 'IP 범위 개수'
            : '수량 열 합산'}{' '}
        · 비교 항목 {report.comparisons.length}개 · 사용자가 제외한 행{' '}
        {report.excluded_rows}개 · 기준 버전 {report.sha256.slice(0, 12)}
      </Typography>
      {report.comparisons.length === 0 && (
        <Alert severity="info">
          비교 가능한 기준 수량이 없습니다. 아래 확인 불가 항목을 확인해주세요.
        </Alert>
      )}
      {report.comparisons
        .slice((currentPage - 1) * 12, currentPage * 12)
        .map((item, index) => (
          <Paper variant="outlined" key={index} sx={{ p: 2, minWidth: 0 }}>
            <Stack spacing={1.5}>
              <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                {item.floor || '현장 전체'} · {item.names.join(', ')}
              </Typography>
              <Typography>
                기준 {item.expected}
                {item.unit} · 문서 {item.rows.join(', ')}행
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <QuantityResult
                  label="맵 객체"
                  check={item.map}
                  unit={item.unit}
                />
                <QuantityResult
                  label="NVR CCTV"
                  check={item.nvr}
                  unit={item.unit}
                />
                <QuantityResult
                  label="등록 자재"
                  check={item.materials}
                  unit={item.unit}
                />
              </Stack>
            </Stack>
          </Paper>
        ))}
      {report.comparisons.length > 12 && (
        <Pagination
          count={Math.ceil(report.comparisons.length / 12)}
          page={currentPage}
          onChange={(_, value) => setPage(value)}
          siblingCount={0}
          size="small"
        />
      )}
    </Stack>
  );
};

export default ReferenceReport;
